import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate, API_BASE } from "@/lib/api";
import { CONTRACT_STATUSES } from "@/lib/constants";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { ValBadge } from "@/lib/validation";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";
import { SendEmailDialog } from "@/components/SendEmailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, FileSignature, MoreVertical, Pencil, Trash2, Download, Eye, Sparkles, Loader2, Braces, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Contracts() {
  const [items, setItems] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [emailDoc, setEmailDoc] = useState(null);

  const load = () => api.get("/contracts").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/client-projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/contract-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const openNew = () => { api.get("/contract-templates").then((r) => setTemplates(r.data)).catch(() => {}); setForm({ client_id: "", client_name: "", client_project_id: "", template_id: "", montant: 0, date_debut: new Date().toISOString().slice(0, 10), date_fin: "", body: "", statut: "À signer" }); setOpen(true); };
  const openEdit = (c) => { api.get("/contract-templates").then((r) => setTemplates(r.data)).catch(() => {}); setForm({ ...c, body: c.body || c.conditions || "" }); setOpen(true); };

  const fillVariables = (text) => {
    if (!text) return text;
    const client = clients.find((c) => c.id === form.client_id);
    const proj = projects.find((p) => p.id === form.client_project_id);
    const vals = {
      client: client?.nom || form.client_name || "",
      projet: proj?.nom || form.client_project_name || "",
      montant: form.montant ? `${Number(form.montant).toLocaleString()} FCFA` : "",
      adresse: client?.adresse || "",
      nif: client?.nif || "",
      debut: form.date_debut ? fmtDate(form.date_debut) : "",
      fin: form.date_fin ? fmtDate(form.date_fin) : "",
    };
    const rules = [
      [/\[(nom (du |de la )?client|client|nom client)\]/gi, vals.client],
      [/\[(nom (du )?projet|projet( client)?)\]/gi, vals.projet],
      [/\[(montant(\s?(du|de) contrat| total)?)\]/gi, vals.montant],
      [/\[(adresse( (du|de la) client)?)\]/gi, vals.adresse],
      [/\[nif( (du|de la) client)?\]/gi, vals.nif],
      [/\[(date (de )?d[ée]but)\]/gi, vals.debut],
      [/\[(date (de )?fin)\]/gi, vals.fin],
    ];
    let out = text;
    rules.forEach(([re, val]) => { if (val) out = out.replace(re, val); });
    return out;
  };
  const applyVariables = () => setForm((p) => ({ ...p, body: fillVariables(p.body) }));

  const pickClient = (v) => { const c = clients.find((x) => x.id === v); setForm((p) => ({ ...p, client_id: v, client_name: c ? c.nom : p.client_name })); };
  const pickProject = (v) => { const pr = projects.find((x) => x.id === v); setForm((p) => ({ ...p, client_project_id: v, client_project_name: pr ? pr.nom : "", projet: pr ? pr.nom : p.projet })); };
  const pickTemplate = (v) => { const t = templates.find((x) => x.id === v); setForm((p) => ({ ...p, template_id: v, body: t ? fillVariables(t.body) : p.body })); };

  const runAI = async () => {
    setAiBusy(true);
    const client = clients.find((c) => c.id === form.client_id);
    const proj = projects.find((p) => p.id === form.client_project_id);
    const ctx = `Client: ${client?.nom || form.client_name || "N/A"}. Projet: ${proj?.nom || "N/A"}. Montant: ${form.montant || 0} FCFA.`;
    try {
      const token = localStorage.getItem("omnibat_token");
      const res = await fetch(`${API_BASE}/contracts/ai-generate`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: `Génère un contrat pour ce contexte. ${ctx}`, mode: form.body ? "adapt" : "generate", current: form.body || "" }),
      });
      if (!res.ok || !res.body) throw new Error("Échec IA");
      const reader = res.body.getReader(); const dec = new TextDecoder(); let acc = "";
      while (true) { const { done, value } = await reader.read(); if (done) break; acc += dec.decode(value, { stream: true }); setForm((p) => ({ ...p, body: acc })); }
      toast.success("Contrat généré par l'IA");
    } catch (e) { toast.error("Génération IA échouée"); } finally { setAiBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (form.id) await api.put(`/contracts/${form.id}`, form);
      else await api.post("/contracts", form);
      toast.success("Contrat enregistré"); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/contracts/${id}`); toast.success("Supprimé"); load(); };

  const projectOptions = projects.filter((p) => !form?.client_id || p.client_id === form.client_id);

  return (
    <div>
      <PageHeader title="Contrats" subtitle="Génération de contrats · modèles réutilisables & assistance IA">
        <Button onClick={openNew} data-testid="add-contract" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau contrat</Button>
      </PageHeader>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={FileSignature} title="Aucun contrat" subtitle="Créez un contrat depuis un modèle ou avec l'IA." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Projet</TableHead>
              <TableHead>Montant</TableHead><TableHead>Début</TableHead><TableHead>Statut</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`contract-row-${c.id}`}>
                  <TableCell className="font-mono text-xs text-slate-500">{c.numero}</TableCell>
                  <TableCell className="font-semibold">{c.client_name}</TableCell>
                  <TableCell className="text-slate-600">{c.client_project_name || c.projet || "—"}</TableCell>
                  <TableCell className="font-mono text-xs font-bold">{fmtMoney(c.montant)}</TableCell>
                  <TableCell className="text-sm">{c.date_debut ? fmtDate(c.date_debut) : c.date ? fmtDate(c.date) : "—"}</TableCell>
                  <TableCell><StatusBadge value={c.statut} /><ValBadge v={c.validation} /></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`contract-menu-${c.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setPreview({ path: `/contracts/${c.id}/pdf`, filename: `${c.numero}.pdf` })} data-testid={`contract-preview-${c.id}`}><Eye className="h-4 w-4 mr-2" />Aperçu avant impression</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPreview({ path: `/contracts/${c.id}/pdf`, filename: `${c.numero}.pdf` })}><Download className="h-4 w-4 mr-2" />Télécharger PDF</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEmailDoc(c)} data-testid={`contract-email-${c.id}`}><Mail className="h-4 w-4 mr-2" />Envoyer par e-mail</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(c)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(c.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? `Contrat ${form.numero || ""}` : "Nouveau contrat"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Client</Label>
                  <Select value={form.client_id || ""} onValueChange={pickClient}>
                    <SelectTrigger className="mt-1" data-testid="contract-client"><SelectValue placeholder={form.client_name || "Sélectionner"} /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Projet client</Label>
                  <Select value={form.client_project_id || ""} onValueChange={pickProject}>
                    <SelectTrigger className="mt-1" data-testid="contract-project"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{projectOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Modèle de contrat</Label>
                  <Select value={form.template_id || ""} onValueChange={pickTemplate}>
                    <SelectTrigger className="mt-1" data-testid="contract-template"><SelectValue placeholder="Aucun / vierge" /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.titre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Montant (FCFA)</Label><Input type="number" value={form.montant} onChange={(e) => setForm({ ...form, montant: Number(e.target.value) })} className="mt-1" /></div>
                <div><Label className="text-xs">Date début</Label><Input type="date" value={form.date_debut || ""} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Date fin</Label><Input type="date" value={form.date_fin || ""} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} className="mt-1" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Statut</Label>
                  <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}><SelectTrigger className="mt-1" data-testid="contract-statut"><SelectValue /></SelectTrigger>
                    <SelectContent>{CONTRACT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-xs">Texte du contrat (modifiable avant validation)</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="ghost" disabled={!form.body} onClick={applyVariables} data-testid="contract-fill-vars" className="text-slate-600"><Braces className="h-4 w-4 mr-1.5" />Remplir les variables</Button>
                  <Button type="button" size="sm" variant="outline" disabled={aiBusy} onClick={runAI} data-testid="contract-ai" className="text-brand-700 border-brand-200">{aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" />{form.body ? "Adapter avec l'IA" : "Générer avec l'IA"}</>}</Button>
                </div>
              </div>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={12} data-testid="contract-body" className="font-mono text-xs" placeholder="Sélectionnez un modèle, ou générez le contrat avec l'IA, puis ajustez le texte ici." />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={saving} data-testid="contract-submit" className="bg-brand-600 hover:bg-brand-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer le contrat"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)} path={preview?.path} filename={preview?.filename} title="Aperçu du contrat" />
      <SendEmailDialog open={!!emailDoc} onOpenChange={(o) => !o && setEmailDoc(null)} endpoint={`/contracts/${emailDoc?.id}/send-email`} docLabel="Le contrat" numero={emailDoc?.numero} />
    </div>
  );
}
